import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { format } from 'date-fns/esm';
import { Observable, Subscription } from 'rxjs';
import { ContratoService } from 'src/app/contrato/contrato.service';
import { TipoFuncionalidade, TipoValidacaoCadastral, ValidacaoCadastroModalComponent, ValidacaoCadastroModalData } from 'src/app/shared/components/validacao-cadastro-modal/validacao-cadastro-modal.component';
import { StatusParcelaEnum } from 'src/app/shared/enums/status-parcela.enum';
import { LoaderService } from 'src/app/shared/loader/loader.service';
import { Contrato } from 'src/app/shared/models/contrato.model';
import { FuncionalidadeEnum } from 'src/app/shared/models/funcionalidade-enum';
import { ModalWrapperData } from 'src/app/shared/models/modal-wrapper-data.model';
import { StatusLinkPagamentoCelerEnum } from 'src/app/shared/models/pagamento-pix-celer-model';
import { Parcela } from 'src/app/shared/models/parcela.model';
import { ValidacaoCadastro } from 'src/app/shared/models/validacao-cadastro.model';
import { BaseModalService } from 'src/app/shared/services/base-modal.service';
import { GoogleAnalyticsService } from 'src/app/shared/services/google-analytics.service';
import { ToastService } from 'src/app/shared/toast';
import DateUtils from 'src/app/shared/utils/date-utils';

@Component({
  selector: 'app-installment-list-page',
  templateUrl: './installment-list-page.component.html',
  styleUrls: ['./installment-list-page.component.scss']
})
export class InstallmentListPageComponent implements OnInit, OnDestroy, AfterViewInit {

  subscriptions = new Subscription();
  contractNumber: string;
  contract: Contrato;
  installments: Parcela[];
  selectedInstallment: Parcela;
  validateRegistrationData: ValidacaoCadastro;
  validationModalRef: MatDialogRef<any>;
  isMobileMatches: Observable<BreakpointState> = this.breakpointObserver.observe('(max-width: 768px)');

  @ViewChild('payButton') payButton: ElementRef<HTMLDivElement>;
  @ViewChild('stickySentinel') stickySentinel: ElementRef;
  isSticky: boolean;
  checkoutType: 'pix' | 'boleto' | null = 'boleto';

  // Trocar usar o length do array quando puder selecionar multiplas parcelas.
  readonly FIXED_SELECTED_INSTALLMENT_LENGTH = 1;
  isMobile: boolean;
  permitirPagamentoPix = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private renderer: Renderer2,
    private googleAnalyticsService: GoogleAnalyticsService,
    private contratoService: ContratoService,
    private baseModalService: BaseModalService,
    private loader: LoaderService,
    private toast: ToastService,
  ) { }

  ngOnInit(): void {
    this.subscriptions.add(this.route.params.subscribe(params => {
      const { idContrato } = params;
      this.contractNumber = idContrato;
      this.verificarPermissaoPagamentoPix(idContrato);
      this.fetchContract(this.contractNumber);
    }));

  }

  private observer: IntersectionObserver;

  ngAfterViewInit() {
    this.isMobileMatches.subscribe(state => {
      this.isMobile = state.matches;
      if (state.matches) {
        this.configPositionSticky();
      } else {
        this.removePositionSticky();
      }
    });
  }

  configPositionSticky() {
    this.observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        this.isSticky = false;
        this.renderer.removeClass(this.payButton.nativeElement, 'fixed');
      } else {
        this.isSticky = true;
        this.renderer.addClass(this.payButton.nativeElement, 'fixed');
      }
    });

    this.observer.observe(this.stickySentinel.nativeElement);
  }

  removePositionSticky() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      this.renderer.removeClass(this.payButton.nativeElement, 'fixed');
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.removePositionSticky();
    if (this.validationModalRef) {
      this.validationModalRef.close({ closeFromRedirect: true });
    }
  }

  private async fetchContract(idContrato: string): Promise<void> {
    try {
      this.loader.start();
      const { parcelas, ...contract } = await this.contratoService.getContrato(idContrato);
      this.installments = parcelas;
      this.contract = contract;
      this.sendGTMEvent();

      const someInstallmentIsDueDate = this.installments.some(installment => installment.statusParcela === StatusParcelaEnum.STATUS_VENCIDA);

      const page_name = someInstallmentIsDueDate ? '/renegocie/portal/servicos/carne/segunda_via/em_atraso' : '/renegocie/portal/servicos/carne/segunda_via/em_dia';

      this.googleAnalyticsService.pushPageViewEvent({
        page_name,
        page_template: 'renegocie',
      })
    } catch (error) {
      this.googleAnalyticsService.pushActionEvent({
        event: 'view_error',
        detail: error?.error?.message ?? 'Erro ao buscar o contrato.',
      });
      this.toast.show({ type: 'error', title: 'Erro ao buscar o contrato.', text: 'Por favor tente novamente mais tarde.' })
    } finally {
      this.loader.stop();
    }
  }

  async verificarPermissaoPagamentoPix(contratoId: string) {
    try {
      this.loader.start();
      const res = await this.contratoService.verificarElegibilidadePagamentoCeler(contratoId);
      this.permitirPagamentoPix = res.elegivel;
      this.checkoutType = this.permitirPagamentoPix ? 'pix' : 'boleto';
    } catch (error) {
      this.googleAnalyticsService.pushActionEvent({
        event: 'view_error',
        detail: error?.error?.message ?? 'Erro ao verificar permissão de pagamento Pix',
      });

      this.permitirPagamentoPix = false;
    } finally {
      this.loader.stop();
    }
  }

  async handlePayInstallment() {
    if (this.isMobile && this.isSticky) {
      document.getElementById("sticky-sentinel").scrollIntoView({ behavior: "smooth" });
      return;
    }

    const canProceed = await this.validateRegistration();

    if (canProceed) {
      this.router.navigate([`/portal/contratos/${this.contractNumber}/segundavia/${this.selectedInstallment.numeroParcela}/checkout/${this.checkoutType}`]);
    }

    const isInstallmentOnTime = this.selectedInstallment?.statusParcela !== StatusParcelaEnum.STATUS_VENCIDA;

    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'pagar',
      context: isInstallmentOnTime ? 'portal reneg:em dia' : 'portal reneg:em atraso'
    });
  }

  allowGenerateSecondCopy(parcela: Parcela): boolean {
    const { statusParcela, linkPagamento } = parcela;

    if (this.contract.funcionarioFolhaPagamento) {
      return false;
    }

    if (linkPagamento?.statusLinkPagamento === StatusLinkPagamentoCelerEnum.PAGO) {
      return false;
    }

    if (!this.contract.renegociavelAcordoParcela || statusParcela === StatusParcelaEnum.STATUS_PAGO || statusParcela === StatusParcelaEnum.STATUS_FORA_RANGE_ACORDO) {
      return false;
    }

    return true;
  }

  async validateRegistration() {
    try {
      this.validateRegistrationData = await this.contratoService.validarCadastro(FuncionalidadeEnum.SEGUNDA_VIA);

      if (this.validateRegistrationData.validarCadastro) {
        const modalResponse = await this.showValidationModal();
        return modalResponse?.valido;
      }

      return true;
    } catch (response) {
      this.googleAnalyticsService.pushActionEvent({
        event: 'view_error',
        detail: response?.error ? response.error.mensagem : 'Por favor tente novamente mais tarde.',
      });

      this.toast.show({
        title: 'Erro inesperado.',
        text: response?.error ? response.error.mensagem : 'Por favor tente novamente mais tarde.',
        type: 'error'
      })
    } finally {
    }
  }

  private getModalData(): ModalWrapperData<ValidacaoCadastroModalData> {
    return {
      title: 'Verificação de segurança',
      component: ValidacaoCadastroModalComponent,
      config: { disableClose: true, autoFocus: false },
      data: {
        funcionalidade: TipoFuncionalidade.VALIDACAO,
        tipo: TipoValidacaoCadastral.PHONE,
        initialValue: this.validateRegistrationData.telefoneCadastrado,
      }
    };
  }

  private async showValidationModal() {
    const data = this.getModalData();
    this.validationModalRef = this.baseModalService.showModal(data);
    const modalResponse = await this.validationModalRef.afterClosed().toPromise();

    if (modalResponse?.showConfirmationModal) {
      const confirm = await this.baseModalService.showConfirmationModal({
        text: 'Não conseguimos validar o número do celular informado. <strong>Atualize o número do celular no seu perfil.</strong>',
        confirmBtnText: 'Ir para o meu perfil',
        deniedBtnText: 'Agora não',
      }).afterClosed().toPromise();

      if (confirm) {
        this.router.navigate([`/portal/perfil`]);
      }
    }

    return modalResponse;
  }

  handleChangeSelectedInstallment(installment: Parcela) {
    this.selectedInstallment = installment;
    this.checkoutType = this.permitirPagamentoPix ? 'pix' : 'boleto';

    const isInstallmentOnTime = installment.statusParcela !== StatusParcelaEnum.STATUS_VENCIDA;

    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: 'selecionar parcela',
      context: isInstallmentOnTime ? 'portal reneg:em dia' : 'portal reneg:em atraso'
    })
  }

  sendGTMEvent() {
    this.googleAnalyticsService.pushEvent({
      event: 'update',
      pagina: {
        nomePagina: '/renegociacao/contratos/segundavia',
        templatePagina: 'renegociacao',
        contratos: {
          numeroContrato: this.contract.contrato,
          dataVencimento: format(DateUtils.normalizedDate(this.contract.vencimento), 'dd/MM/yyyy'),
          saldo: this.contract.saldo,
          parcelasPagas: this.installments.reduce((acc, parcela) => parcela.statusParcela === StatusParcelaEnum.STATUS_PAGO ? acc += 1 : acc, 0),
          situacao: this.googleAnalyticsService.situacaoContratoPipe(this.contract.situacao),
          mercadoria: this.contract?.mercadoria.mercadorias.join(' / '),
        }
      }
    });
  }

  handleCheckoutType(checkoutType: 'pix' | 'boleto') {
    this.checkoutType = checkoutType;
    const isInstallmentOnTime = this.selectedInstallment?.statusParcela !== StatusParcelaEnum.STATUS_VENCIDA;
    this.googleAnalyticsService.pushActionEvent({
      event: 'click',
      detail: `pagar com ${checkoutType}`,
      context: isInstallmentOnTime ? 'portal reneg:em dia' : 'portal reneg:em atraso'
    })
  }

}
